import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles, Hourglass, ShieldCheck, ShieldAlert, FileText, Eye, CheckCircle2, X, RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const GROUPS = [
  { id: "senior", label: "Sénior (65+)" },
  { id: "mineur", label: "Mineur (-18)" },
  { id: "jeune_adulte", label: "Jeune adulte (18-25)" },
  { id: "etudiant", label: "Étudiant" },
  { id: "famille_nombreuse", label: "Famille nombreuse" },
  { id: "pmr", label: "PMR" },
  { id: "demandeur_emploi", label: "Demandeur d'emploi" },
];

const FILTERS = [
  { id: "pending", label: "À valider", color: "bg-sun-100 text-sun-700" },
  { id: "verified", label: "Validés", color: "bg-mint-100 text-mint-700" },
  { id: "rejected", label: "Refusés", color: "bg-pink-100 text-pink-600" },
];

export default function AdminCitizens() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [validateFor, setValidateFor] = useState(null);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [rejectFor, setRejectFor] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/admin/cni-verifications?status_filter=${filter}`)
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const openValidate = (u) => {
    setValidateFor(u);
    setSelectedGroups(u.groups || []);
  };

  const confirmValidate = async () => {
    if (!validateFor) return;
    if (selectedGroups.length === 0 && !window.confirm("Aucun groupe sélectionné — l'utilisateur n'aura accès à aucun avantage. Continuer ?")) {
      return;
    }
    setBusy(validateFor.user_id);
    try {
      await api.patch(`/admin/cni-verifications/${validateFor.user_id}`, {
        status: "verified",
        groups: selectedGroups,
      });
      toast.success("CNI validée — groupes attribués");
      setValidateFor(null); setSelectedGroups([]);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const confirmReject = async () => {
    if (!rejectFor) return;
    setBusy(rejectFor.user_id);
    try {
      await api.patch(`/admin/cni-verifications/${rejectFor.user_id}`, {
        status: "rejected", reason,
      });
      toast.success("CNI refusée");
      setRejectFor(null); setReason("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const toggleGroup = (g) => {
    setSelectedGroups((cur) => cur.includes(g) ? cur.filter((x) => x !== g) : [...cur, g]);
  };

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 py-8 sm:py-12">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="phenix-eyebrow mb-2 inline-flex items-center gap-1.5">
            <Sparkles size={11} /> Avantages Citoyens
          </div>
          <h1 className="heading-display text-[1.8rem] sm:text-[2.2rem] text-plum-900">Vérification CNI</h1>
          <p className="text-[13px] text-plum-700/65 mt-1">Validez les CNI et attribuez les groupes citoyens pour ouvrir les avantages mairie.</p>
        </div>
        <Button onClick={load} variant="outline" className="rounded-full h-10 px-4 inline-flex items-center gap-1.5">
          <RefreshCw size={13} /> Rafraîchir
        </Button>
      </div>

      <div className="flex gap-2 overflow-x-auto mb-5" data-testid="cni-filters">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`cni-filter-${f.id}`}
            className={`shrink-0 h-10 px-4 rounded-full text-[13px] font-bold transition-all ${
              filter === f.id ? `${f.color} shadow-sm` : "bg-white text-plum-700 border border-cream-200 hover:bg-cream-50"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="phenix-card p-10 animate-pulse h-32" />
      ) : items.length === 0 ? (
        <div className="phenix-card p-10 text-center bg-cream-50/40">
          <FileText size={28} className="mx-auto text-plum-700/30 mb-2" />
          <h3 className="font-display font-bold text-plum-900">
            {filter === "pending" ? "Aucune CNI en attente" : filter === "verified" ? "Aucun citoyen vérifié" : "Aucun refus"}
          </h3>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="cni-list">
          {items.map((u) => (
            <li key={u.user_id} className="phenix-card bg-white p-4 sm:p-5" data-testid={`cni-row-${u.user_id}`}>
              <div className="flex flex-wrap items-start gap-4">
                <button
                  onClick={() => setPreview(u)}
                  className="shrink-0 w-20 h-24 rounded-xl border border-cream-200 overflow-hidden bg-cream-50 grid place-items-center relative group"
                  data-testid={`cni-preview-${u.user_id}`}
                >
                  {u.cni_doc_url?.startsWith("data:image") ? (
                    <img src={u.cni_doc_url} alt="cni" className="w-full h-full object-cover" />
                  ) : (
                    <FileText size={26} className="text-plum-700/40" />
                  )}
                  <span className="absolute inset-0 bg-plum-900/40 opacity-0 group-hover:opacity-100 transition-opacity grid place-items-center text-white">
                    <Eye size={20} />
                  </span>
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h3 className="font-display font-extrabold text-plum-900 text-base">{u.pseudo || u.name}</h3>
                    <span className="phenix-pill bg-cream-100 text-plum-700/75 text-[10px] uppercase">{u.role}</span>
                    {u.cni_status === "pending" && <span className="phenix-pill bg-sun-100 text-sun-700 text-[10px] inline-flex items-center gap-1"><Hourglass size={10} /> En attente</span>}
                    {u.cni_status === "verified" && <span className="phenix-pill bg-mint-100 text-mint-700 text-[10px] inline-flex items-center gap-1"><ShieldCheck size={10} /> Vérifié</span>}
                    {u.cni_status === "rejected" && <span className="phenix-pill bg-pink-100 text-pink-600 text-[10px] inline-flex items-center gap-1"><ShieldAlert size={10} /> Refusé</span>}
                  </div>
                  <p className="text-[12px] text-plum-700/65">{u.email}</p>
                  <p className="text-[11px] text-plum-700/45 mt-0.5">
                    Déposé {u.cni_doc_uploaded_at ? new Date(u.cni_doc_uploaded_at).toLocaleString("fr-FR") : "—"}
                  </p>
                  {u.groups?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {u.groups.map((g) => {
                        const label = GROUPS.find((x) => x.id === g)?.label || g;
                        return <span key={g} className="phenix-pill bg-plum-100 text-plum-700 text-[10px]">{label}</span>;
                      })}
                    </div>
                  )}
                  {u.cni_rejected_reason && (
                    <p className="text-[11px] text-pink-600 mt-1 italic">Motif refus : {u.cni_rejected_reason}</p>
                  )}
                  {u.cni_status === "pending" && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      <Button
                        onClick={() => openValidate(u)}
                        className="rounded-full bg-mint-600 hover:bg-mint-700 text-white h-10 px-4 text-[12px] font-bold inline-flex items-center gap-1.5"
                        data-testid={`cni-validate-${u.user_id}`}
                      >
                        <CheckCircle2 size={13} /> Valider & attribuer les groupes
                      </Button>
                      <Button
                        onClick={() => { setRejectFor(u); setReason(""); }}
                        variant="outline"
                        className="rounded-full h-10 px-4 text-[12px] font-bold inline-flex items-center gap-1.5"
                        data-testid={`cni-reject-${u.user_id}`}
                      >
                        <X size={13} /> Refuser
                      </Button>
                    </div>
                  )}
                  {u.cni_status === "verified" && (
                    <Button
                      onClick={() => openValidate(u)}
                      variant="outline"
                      className="mt-3 rounded-full h-9 px-4 text-[11px] font-bold"
                      data-testid={`cni-reassign-${u.user_id}`}
                    >
                      Modifier les groupes
                    </Button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Preview */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="cni-preview-dialog">
          {preview && (
            <>
              <DialogHeader><DialogTitle>{preview.pseudo || preview.name} — CNI</DialogTitle></DialogHeader>
              <div className="bg-cream-50 rounded-xl p-2">
                {preview.cni_doc_url?.startsWith("data:image") ? (
                  <img src={preview.cni_doc_url} alt="cni" className="w-full max-h-[70vh] object-contain rounded-lg" />
                ) : preview.cni_doc_url?.startsWith("data:application/pdf") ? (
                  <embed src={preview.cni_doc_url} type="application/pdf" className="w-full h-[70vh] rounded-lg" />
                ) : <p className="p-6 text-center text-plum-700/65">Aperçu indisponible.</p>}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Validate + Groups */}
      <Dialog open={!!validateFor} onOpenChange={(o) => !o && setValidateFor(null)}>
        <DialogContent className="max-w-md" data-testid="validate-dialog">
          {validateFor && (
            <>
              <DialogHeader><DialogTitle>Attribuer les groupes</DialogTitle></DialogHeader>
              <p className="text-[13px] text-plum-700/75 mb-3">
                Sélectionnez tous les groupes citoyens qui s&apos;appliquent à <strong>{validateFor.pseudo || validateFor.name}</strong> :
              </p>
              <div className="space-y-1.5 mb-4 max-h-[40vh] overflow-y-auto">
                {GROUPS.map((g) => (
                  <label
                    key={g.id}
                    className={`flex items-center gap-2 p-2.5 rounded-xl border cursor-pointer transition-colors ${
                      selectedGroups.includes(g.id) ? "bg-plum-50 border-plum-300" : "bg-white border-cream-200 hover:bg-cream-50"
                    }`}
                    data-testid={`group-toggle-${g.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedGroups.includes(g.id)}
                      onChange={() => toggleGroup(g.id)}
                      className="rounded text-plum-700"
                    />
                    <span className="text-[14px] text-plum-900 font-semibold">{g.label}</span>
                  </label>
                ))}
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={confirmValidate}
                  disabled={busy === validateFor.user_id}
                  className="flex-1 rounded-full bg-mint-600 hover:bg-mint-700 text-white h-11 font-bold"
                  data-testid="validate-confirm"
                >
                  Valider la CNI
                </Button>
                <Button variant="outline" onClick={() => setValidateFor(null)} className="rounded-full h-11 px-5">
                  Annuler
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject */}
      <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <DialogContent className="max-w-md" data-testid="cni-reject-dialog">
          {rejectFor && (
            <>
              <DialogHeader><DialogTitle>Refuser la CNI</DialogTitle></DialogHeader>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Motif (illisible, non conforme…)" className="rounded-xl bg-cream-50 mb-3" data-testid="cni-reject-reason" />
              <div className="flex gap-2">
                <Button onClick={confirmReject} disabled={busy === rejectFor.user_id || !reason.trim()} className="flex-1 rounded-full bg-pink-500 hover:bg-pink-600 text-white h-11 font-bold" data-testid="cni-confirm-reject">
                  Confirmer
                </Button>
                <Button variant="outline" onClick={() => setRejectFor(null)} className="rounded-full h-11 px-5">Annuler</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
